import { Modal, Pressable, View, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '@/components/ui/Button';
import { Caption, ThemedText } from '@/components/ui/Text';
import { AppImage } from '@/components/ui/AppImage';
import { useTheme } from '@/theme';

type RecapSharePreviewModalProps = {
  visible: boolean;
  imageUri?: string | null;
  tripName: string;
  preparing?: boolean;
  onClose: () => void;
  onShare: () => void;
};

export function RecapSharePreviewModal({
  visible,
  imageUri,
  tripName,
  preparing = false,
  onClose,
  onShare,
}: RecapSharePreviewModalProps) {
  const theme = useTheme();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.overlay,
          justifyContent: 'center',
          padding: theme.spacing(2),
        }}
      >
        <View
          style={{
            maxHeight: '92%',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radii.xl,
            overflow: 'hidden',
            gap: theme.spacing(1),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: theme.spacing(1.5),
              paddingTop: theme.spacing(1.5),
            }}
          >
            <View style={{ flex: 1, paddingRight: theme.spacing(1) }}>
              <Caption color={theme.colors.textSecondary} style={{ textTransform: 'uppercase', fontWeight: '700' }}>
                Share recap
              </Caption>
              <ThemedText variant="bodyStrong" numberOfLines={1}>
                {tripName}
              </ThemedText>
            </View>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Close recap preview"
              style={{
                padding: theme.spacing(0.75),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.backgroundSecondary,
              }}
            >
              <Ionicons name="close" size={18} color={theme.colors.text} />
            </Pressable>
          </View>

          <View
            style={{
              marginHorizontal: theme.spacing(1.5),
              borderRadius: theme.radii.lg,
              overflow: 'hidden',
              backgroundColor: theme.colors.backgroundSecondary,
              minHeight: 420,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {preparing ? (
              <View style={{ alignItems: 'center', gap: theme.spacing(1), padding: theme.spacing(3) }}>
                <ActivityIndicator color={theme.colors.primary} />
                <Caption color={theme.colors.textSecondary}>Preparing your recap image...</Caption>
              </View>
            ) : imageUri ? (
              <AppImage
                source={{ uri: imageUri }}
                resizeMode="contain"
                style={{ width: '100%', height: 520 }}
              />
            ) : null}
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: theme.spacing(1),
              paddingHorizontal: theme.spacing(1.5),
              paddingBottom: theme.spacing(1.5),
            }}
          >
            <Button label="Cancel" variant="ghost" onPress={onClose} disabled={preparing} />
            <Button label="Share image" onPress={onShare} disabled={preparing || !imageUri} />
          </View>
        </View>
      </View>
    </Modal>
  );
}
